import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import VerifyIdentityButton from '../components/Identity/VerifyIdentityButton';
import { mergeSubscriptionTier } from '../utils/subscription';
import {
  IDENTITY_FAILED,
  IDENTITY_VERIFIED,
  isIdentityResolved,
  fetchIdentityProfile,
} from '../utils/identityVerification';
import '../components/Identity/Identity.css';
import '../App.css';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 10;

/**
 * Stripe's return_url. The webhook may not have landed yet, so poll the profile
 * rather than assuming the redirect means success.
 */
const VerificationCompletePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [loadError, setLoadError] = useState('');
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    cancelledRef.current = false;
    let timer = null;
    let attempt = 0;

    const poll = async () => {
      try {
        const { user: fresh, status: next } = await fetchIdentityProfile();
        if (cancelledRef.current) return;
        setLoadError('');
        if (fresh) {
          const withTier = mergeSubscriptionTier(fresh);
          setUser(withTier);
          localStorage.setItem('user', JSON.stringify(withTier));
        }
        setStatus(next);
        if (isIdentityResolved(next)) return;
      } catch {
        if (cancelledRef.current) return;
        setLoadError("We couldn't reach the server to check your status.");
      }

      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const goToNextScreen = () => {
    // Onboarding writes onboardingCompleted; unfinished users go back to finish.
    navigate(user?.onboardingCompleted === true ? '/dashboard' : '/onboarding');
  };

  const isVerified = status === IDENTITY_VERIFIED;
  const isFailed = status === IDENTITY_FAILED;
  const showFailure = isFailed || timedOut;

  return (
    <div className="onboarding-container" data-onboarding-flow="1">
      <div className="onboarding-card">
        <div className="onboarding-content">
          {isVerified && (
            <div className="identity-status-panel">
              <span className="identity-status-icon is-success">
                <ShieldCheck size={28} />
              </span>
              <h2 className="step-title">You&apos;re verified</h2>
              <p className="step-description">
                Your identity has been confirmed. Your Compliance Passport™ now
                carries a verified identity.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-large"
                onClick={goToNextScreen}
              >
                Continue →
              </button>
            </div>
          )}

          {!isVerified && showFailure && (
            <div className="identity-status-panel">
              <span className="identity-status-icon is-warning">
                <AlertTriangle size={28} />
              </span>
              <h2 className="step-title">Verification didn&apos;t complete</h2>
              <p className="step-description">
                {isFailed
                  ? "We couldn't confirm your identity. You can try again — it only takes a couple of minutes."
                  : "We're still waiting on a result from Stripe. You can try again, or continue and check back shortly."}
              </p>
              <VerifyIdentityButton user={user} status={IDENTITY_FAILED} />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={goToNextScreen}
              >
                Continue for now
              </button>
            </div>
          )}

          {!isVerified && !showFailure && (
            <div className="identity-status-panel">
              <span className="identity-status-icon is-progress">
                <Loader2 size={28} className="identity-spin" aria-hidden="true" />
              </span>
              <h2 className="step-title">Finishing up your verification...</h2>
              <p className="step-description">
                This usually takes a few seconds. Hang tight — we&apos;ll update
                this page as soon as Stripe confirms.
              </p>
              {loadError && <p className="identity-status-note">{loadError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationCompletePage;
